import { useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import { useCaseContradictions, useSetContradictionAssessment, type Contradiction, type ContradictionEvidence, type SubFact } from "../../hooks/useCaseContradictions";
import { useMusterContradictions, useContradictionStatus } from "../../hooks/useMusterContradictions";
import { useChunkContext } from "../../hooks/useChunkContext";
import CorpusSearch from "../../components/war-room/CorpusSearch";
import ThreeWayReview, { reviewColor } from "../../components/war-room/ThreeWayReview";
import { useLex } from "../../lib/theme/ThemeProvider";

/**
 * Name the Oathbreakers — the contradiction ledger (Layer 1: "is it a lie?").
 * Review is a single 3-way human call on each sworn allegation:
 *   Accurate · Partially Accurate · Not Accurate.
 * Everything the engine produced (severity, the two-model read, sub-facts, the
 * impeaching evidence) lives behind "details" so the review surface stays calm.
 */
const UNREVIEWED = "#a78bdb";

function OathbreakerLedger() {
  const { caseId } = useParams<{ caseId: string }>();
  const lex = useLex();
  const { data: rows, isLoading, error } = useCaseContradictions(caseId);
  const { muster, mustering, error: mErr, progress } = useMusterContradictions(caseId);
  const { data: status } = useContradictionStatus(caseId);

  const [onlyPending, setOnlyPending] = useState(false);

  let pending = 0, notAccurate = 0, partial = 0, accurate = 0;
  for (const r of rows ?? []) {
    switch (r.human_assessment) {
      case "not_accurate": notAccurate++; break;
      case "partial": partial++; break;
      case "accurate": accurate++; break;
      default: pending++;
    }
  }
  const reviewed = notAccurate + partial + accurate;
  const shown = onlyPending ? (rows ?? []).filter((r) => !r.human_assessment) : (rows ?? []);

  return (
    <CommandLayout caseId={caseId} variant="ctafinal" currentCommand="oathbreaker">
      <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">{`// ${lex.commands.oathbreaker.verb.toLowerCase()}`}</p>
        <h2 className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
          A verified complaint is an oath. Find where it breaks.
        </h2>
        <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
          Each sworn allegation is tested against the evidence record by two models — one proposes, one
          cross-examines. For every flagged allegation you make one call: is it
          <span style={{ color: reviewColor("accurate") }}> accurate</span>,
          <span style={{ color: reviewColor("partial") }}> partially accurate</span>, or
          <span style={{ color: reviewColor("not_accurate") }}> not accurate</span>. Only the partial
          and not-accurate calls arm the filings.
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
          {status?.last_built && !mustering && (
            <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
              last run {new Date(status.last_built).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              {` · ${status.assessed}/${status.total} allegations`}
            </span>
          )}
          {mustering && progress && (
            <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-ember-bright">
              testing {progress.assessed}/{progress.total} · {progress.contradicted} broken so far
            </span>
          )}
          <button
            type="button"
            disabled={!caseId || mustering}
            onClick={() => muster()}
            className={`rounded border px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] transition-colors ${
              mustering
                ? "border-ember-bright/40 bg-ember/10 text-ember-bright"
                : "border-ember-bright/50 bg-ember/10 text-bone hover:bg-ember/30 hover:text-ember-bright"
            }`}
          >
            {mustering ? lex.commands.oathbreaker.pending : `⚖ ${lex.commands.oathbreaker.action}`}
          </button>
        </div>
        {mErr && <p className="mt-2 font-display text-base text-ember-bright">// muster failed: {mErr}</p>}

        {(rows?.length ?? 0) > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 font-display text-base font-light uppercase tracking-[0.1em]">
            <Tally label="not accurate" n={notAccurate} color={reviewColor("not_accurate")} />
            <Tally label="partial" n={partial} color={reviewColor("partial")} />
            <Tally label="accurate" n={accurate} color={reviewColor("accurate")} />
            <span className="text-bone-mute/60">· {reviewed}/{rows?.length} reviewed</span>
            <button
              type="button"
              onClick={() => setOnlyPending((pp) => !pp)}
              className={`flex items-center gap-1.5 rounded border px-2 py-0.5 transition-colors ${
                onlyPending ? "border-ember-bright/60 bg-ember/15 text-bone" : "border-steel-dark/70 text-bone-mute hover:text-bone"
              }`}
            >
              <i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: UNREVIEWED }} />
              {pending} to review{onlyPending ? " · showing" : " · filter"}
            </button>
          </div>
        )}
      </header>

      <div className="space-y-2">
        {isLoading && <p className="font-display text-base text-bone-mute">// reading the oath…</p>}
        {error && <p className="font-display text-base text-ember-bright">// {error.message}</p>}
        {!isLoading && !error && (rows?.length ?? 0) === 0 && (
          <p className="rounded border border-steel-dark bg-iron/70 px-5 py-6 font-display text-base text-bone-mute">
            // no contradictions logged yet — name the oathbreakers to test every sworn allegation
          </p>
        )}
        {!isLoading && !error && onlyPending && shown.length === 0 && (rows?.length ?? 0) > 0 && (
          <p className="rounded border border-steel-dark bg-iron/70 px-5 py-6 font-display text-base text-bone-mute">
            // nothing left to review — every flagged allegation has your call
          </p>
        )}
        {shown.map((c) => <ContradictionCard key={c.id} c={c} />)}
      </div>
    </CommandLayout>
  );
}

const SEV: Record<string, string> = { perjury_grade: "#b3503f", rule_11: "#c9a23a", impeachment: "#5b9bd5" };
const sev = (k: string | null): string => (k && SEV[k]) || "#5a6470";
const SEV_LABEL: Record<string, string> = { perjury_grade: "perjury-grade", rule_11: "rule 11", impeachment: "impeachment" };

const VERDICT_COLOR: Record<string, string> = {
  contradicted: "#b3503f", tension: "#c9a23a", consistent: "#5a8f6b", no_evidence: "#5a6470", not_factual: "#6b7480",
};
const vColor = (k: string | null | undefined): string => (k && VERDICT_COLOR[k]) || "#5a6470";
const VERDICT_LABEL: Record<string, string> = {
  contradicted: "contradicted", tension: "tension", consistent: "consistent", no_evidence: "no evidence", not_factual: "not factual",
};
const vLabel = (k: string | null | undefined): string => (k && (VERDICT_LABEL[k] ?? k)) || "—";

const TYPE_LABEL: Record<string, string> = {
  affiant_own_statement: "affiant's own statement", knowledge_defeats: "defeats “without knowledge”",
  document_contradiction: "document contradiction", witness_contradiction: "witness contradiction",
};
const MODEL_LABEL: Record<string, string> = { proposer: "GPT-5.5 · proposer", judge: "Claude Opus 4.8 · cross-examiner" };
const CLAMP2: CSSProperties = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };
const CLAMP1: CSSProperties = { display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" };

function Tally({ label, n, color }: { label: string; n: number; color: string }) {
  return (
    <span className="flex items-center gap-1.5 text-bone-mute">
      <i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      {n} {label}
    </span>
  );
}

function ContradictionCard({ c }: { c: Contradiction }) {
  const { caseId } = useParams<{ caseId: string }>();
  const set = useSetContradictionAssessment(caseId);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(c.human_note ?? "");
  const color = c.human_assessment ? reviewColor(c.human_assessment) : UNREVIEWED;
  const dimmed = !!c.human_assessment;
  const subFacts = c.sub_facts ?? [];
  const factual = subFacts.filter((s) => s.verdict === "contradicted" || s.verdict === "tension");
  const choose = (v: string) => { set.mutate({ id: c.id, assessment: v, note: note.trim() || undefined }); setOpen(false); };

  return (
    <div className={`overflow-hidden rounded border border-steel-dark bg-iron/70 backdrop-blur-sm ${dimmed ? "opacity-70" : ""}`}>
      <div className="border-l-2 px-5 py-3" style={{ borderColor: color }}>
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-3 text-left">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className="font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute">&para; {c.paragraph_number}</span>
              {!c.human_assessment && (
                <span className="font-display text-sm font-light uppercase tracking-[0.1em]" style={{ color: UNREVIEWED }}>· needs your call</span>
              )}
            </div>
            <p className="font-display text-[13px] italic leading-snug text-bone" style={open ? undefined : CLAMP2}>
              &ldquo;{c.allegation_text}&rdquo;
            </p>
            {c.rationale && !open && (
              <p className="mt-1 font-display text-[12px] leading-snug text-bone-mute/70" style={CLAMP1}>{c.rationale}</p>
            )}
          </div>
          <span className="mt-0.5 shrink-0 font-display text-sm text-bone-mute">{open ? "▾ details" : "▸ details"}</span>
        </button>

        <div className="mt-2.5">
          <ThreeWayReview value={c.human_assessment} disabled={set.isPending} onChange={choose} />
          {set.isError && <p className="mt-1 font-display text-sm text-ember-bright">// {(set.error as Error).message}</p>}
        </div>
      </div>

      {open && (
        <div className="border-l-2 px-5 pb-4" style={{ borderColor: color }}>
          {c.rationale && <p className="mt-2 font-display text-[12px] leading-snug text-bone-dim">{c.rationale}</p>}

          <textarea value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => { if ((note.trim() || null) !== (c.human_note ?? null) && c.human_assessment) choose(c.human_assessment); }} rows={1}
            placeholder="note — why (saved with your call; calibrates the next run)"
            className="mt-2 w-full resize-y rounded border border-steel-dark/60 bg-black/30 px-2 py-1 font-display text-[12px] text-bone placeholder:text-bone-mute/50" />

          <CorpusSearch seed={c.allegation_text ?? ""} />

          {subFacts.length > 0 ? (
            <div className="mt-3 space-y-2">
              <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/70">
                // the sworn facts in this allegation — {factual.length}/{subFacts.length} flagged
              </p>
              {subFacts.map((s, i) => <SubFactRow key={i} s={s} />)}
            </div>
          ) : (
            c.evidence.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/70">// contradicted by</p>
                {c.evidence.map((e) => <EvidenceRow key={e.id} e={e} />)}
              </div>
            )
          )}

          <TwoModelPanel c={c} />
        </div>
      )}
    </div>
  );
}

function TwoModelPanel({ c }: { c: Contradiction }) {
  if (!c.proposer_verdict && !c.judge_verdict) return null;
  const disagree = c.review_status === "split"
    || (!!c.proposer_verdict && !!c.judge_verdict && c.proposer_verdict !== c.judge_verdict);
  return (
    <div className="mt-3 rounded border px-3 py-2"
         style={{ borderColor: disagree ? UNREVIEWED : "rgba(90,100,112,0.4)", background: disagree ? "rgba(167,139,219,0.06)" : "transparent" }}>
      <p className="mb-1.5 font-display text-sm font-light uppercase tracking-[0.12em]"
         style={{ color: disagree ? UNREVIEWED : "rgba(220,220,220,0.5)" }}>
        // two-model read{disagree ? " — they disagreed" : " — they agreed"}
        {c.severity ? ` · engine grade: ${SEV_LABEL[c.severity] ?? c.severity}` : ""}
      </p>
      <div className="grid gap-2 md:grid-cols-2">
        <ModelRead who="proposer" verdict={c.proposer_verdict} rationale={c.proposer_rationale} />
        <ModelRead who="judge" verdict={c.judge_verdict} rationale={c.judge_rationale} />
      </div>
    </div>
  );
}

function ModelRead({ who, verdict, rationale }: { who: "proposer" | "judge"; verdict: string | null; rationale: string | null }) {
  return (
    <div className="rounded border border-steel-dark/50 bg-black/20 px-2.5 py-2">
      <div className="mb-1 flex items-center gap-2">
        <span className="font-display text-[11px] font-light uppercase tracking-[0.1em] text-bone-mute/70">{MODEL_LABEL[who]}</span>
        <span className="rounded px-1.5 py-0.5 font-display text-[11px] font-light uppercase tracking-[0.08em]" style={{ background: vColor(verdict), color: "#0c0f12" }}>
          {vLabel(verdict)}
        </span>
      </div>
      {rationale && <p className="font-display text-[12px] leading-snug text-bone-dim">{rationale}</p>}
    </div>
  );
}

function SubFactRow({ s }: { s: SubFact }) {
  const [open, setOpen] = useState(s.verdict === "contradicted");
  const color = vColor(s.verdict);
  const split = s.review_status === "split";
  const hasDetail = !!s.rationale || (s.evidence?.length ?? 0) > 0 || (!!s.proposer_verdict && !!s.judge_verdict);
  return (
    <div className="rounded border border-steel-dark/60 bg-black/20">
      <button type="button" onClick={() => setOpen((o) => !o)} className="block w-full text-left hover:bg-iron-2/30">
        <div className="flex items-start gap-2 border-l-2 px-3 py-2" style={{ borderColor: split ? UNREVIEWED : color }}>
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em]" style={{ background: s.severity ? sev(s.severity) : color, color: "#0c0f12" }}>
                {s.severity ? (SEV_LABEL[s.severity] ?? s.severity) : vLabel(s.verdict)}
              </span>
              {split && (
                <span className="rounded px-1.5 py-0.5 font-display text-[11px] font-light uppercase tracking-[0.1em]" style={{ background: UNREVIEWED, color: "#0c0f12" }}>split</span>
              )}
              {s.contradiction_type && (
                <span className="rounded border border-steel-dark/70 px-1.5 py-0.5 font-display text-[11px] font-light uppercase tracking-[0.12em] text-bone-dim">
                  {TYPE_LABEL[s.contradiction_type] ?? s.contradiction_type}
                </span>
              )}
              {(s.evidence?.length ?? 0) > 0 && (
                <span className="font-display text-[11px] font-light uppercase tracking-[0.08em] text-bone-mute/70">
                  {s.evidence.length} cite{s.evidence.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <p className="font-display text-[12.5px] leading-snug text-bone">{s.assertion}</p>
          </div>
          {hasDetail && <span className="mt-0.5 shrink-0 font-display text-sm text-bone-mute">{open ? "▾" : "▸"}</span>}
        </div>
      </button>
      {open && hasDetail && (
        <div className="border-l-2 px-3 pb-2.5" style={{ borderColor: split ? UNREVIEWED : color }}>
          {s.proposer_verdict && s.judge_verdict && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-[11px] font-light uppercase tracking-[0.08em] text-bone-mute/70">
              <span className="flex items-center gap-1">proposer
                <span className="rounded px-1 py-0.5" style={{ background: vColor(s.proposer_verdict), color: "#0c0f12" }}>{vLabel(s.proposer_verdict)}</span></span>
              <span className="flex items-center gap-1">cross-exam
                <span className="rounded px-1 py-0.5" style={{ background: vColor(s.judge_verdict), color: "#0c0f12" }}>{vLabel(s.judge_verdict)}</span></span>
            </div>
          )}
          {s.rationale && <p className="mt-1.5 font-display text-[12px] leading-snug text-bone-dim">{s.rationale}</p>}
          {(s.evidence?.length ?? 0) > 0 && (
            <div className="mt-2 space-y-2">{s.evidence.map((e, i) => <EvidenceRow key={i} e={e} />)}</div>
          )}
        </div>
      )}
    </div>
  );
}

function EvidenceRow({ e }: { e: Omit<ContradictionEvidence, "id"> }) {
  const [open, setOpen] = useState(false);
  const ctx = useChunkContext(e.chunk_id, open);
  const sColor = e.strength === "strong" ? "#e8703a" : e.strength === "weak" ? "#6b7480" : "#c9a23a";
  return (
    <div className="rounded border border-steel-dark/60 bg-black/20 px-3 py-2">
      <div className="mb-1 flex flex-wrap items-center gap-2 font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute/80">
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: sColor }} />
        <span>{e.source_label ?? "evidence"}</span>
        {e.occurred_on && <span>· {e.occurred_on}</span>}
        {e.strength && <span style={{ color: sColor }}>· {e.strength}</span>}
      </div>
      {e.quote && <p className="border-l-2 border-steel-dark pl-2 font-display text-[12px] leading-snug text-bone-dim">{e.quote}</p>}
      {e.thrust && <p className="mt-1 font-display text-base leading-snug text-bone-mute">{e.thrust}</p>}
      {e.chunk_id && (
        <button type="button" onClick={() => setOpen((o) => !o)}
          className="mt-1.5 font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright/80 hover:text-blue-bright">
          {open ? "▾ hide source" : "▸ view in source"}
        </button>
      )}
      {open && (
        <div className="mt-2 rounded border border-steel-dark/50 bg-charcoal/60 p-2">
          {ctx.isLoading && <p className="font-display text-sm text-bone-mute">// opening the source…</p>}
          {ctx.error && <p className="font-display text-sm text-ember-bright">// {ctx.error.message}</p>}
          {ctx.data && (
            <div>
              <p className="mb-1.5 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/70">
                {ctx.data.source_label}{ctx.data.evidence_kind ? ` · ${ctx.data.evidence_kind.replace(/_/g, " ")}` : ""}
              </p>
              <div className="space-y-1">
                {ctx.data.chunks.map((line) => (
                  <pre key={line.chunk_index}
                    className={`overflow-x-auto whitespace-pre-wrap rounded px-2 py-1 font-display text-base leading-snug ${
                      line.is_center ? "bg-ember/10 text-bone shadow-ember-soft" : "text-bone-mute/70"}`}>
                    {line.body}
                  </pre>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default OathbreakerLedger;
